import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthStore, selectUser } from '@/stores/authStore'
import type { SchoolAssignment } from '@/features/accounts/types'

function defaultSchoolId(assignments: SchoolAssignment[]): string | null {
  const primary = assignments.find(a => a.is_primary)
  return primary?.school_id ?? assignments[0]?.school_id ?? null
}

function resolveSchoolId(
  assignments: SchoolAssignment[],
  storageKey: string | null,
): string | null {
  if (assignments.length === 0) return null
  if (typeof window === 'undefined' || !storageKey) return defaultSchoolId(assignments)
  const stored = localStorage.getItem(storageKey)
  const valid = stored && assignments.some(a => a.school_id === stored)
  return valid ? stored : defaultSchoolId(assignments)
}

/**
 * Persisted primary school for teacher dashboard data (matches dashboards-design §3 / §4).
 */
export function useTeacherSchoolScope() {
  const user = useAuthStore(selectUser)
  const assignments = useMemo(
    () => (user?.school_assignments ?? []).filter(a => a.is_active),
    [user?.school_assignments],
  )

  const storageKey = user?.id != null ? `dashboard:schoolId:${user.id}` : null

  // Resolve synchronously on first render so dashboard queries fire immediately
  // instead of waiting for an effect pass.
  const [schoolId, setSchoolIdState] = useState<string | null>(() =>
    resolveSchoolId(assignments, storageKey),
  )

  useEffect(() => {
    setSchoolIdState(resolveSchoolId(assignments, storageKey))
  }, [assignments, storageKey])

  const setSchoolId = useCallback(
    (id: string) => {
      setSchoolIdState(id)
      if (storageKey) {
        localStorage.setItem(storageKey, id)
      }
    },
    [storageKey],
  )

  return {
    schoolId,
    setSchoolId,
    assignments,
  }
}
